import { calculateProposalBudget, budgetFxRate, normalizeBudgetRates, BUDGET_FX_UNAVAILABLE } from '@/lib/proposalBudget';
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Check, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { proposalTokens } from './tokens';
import { proposalService } from '@/services/ProposalService';
import { formatCurrency } from '@/lib/currencyFormat';
import './proposal.css';
import { getDisplayedFinancialCostSplit } from './financialCostSplit';

interface Hotel {
  id: string;
  name: string;
  stars: number;
  image: string;
  images?: string[];
  description?: string;
  roomCount?: number;
  meetingSpaces?: string[];
  priceRange?: string;
  highlights?: string[];
  recommended?: boolean;
}

interface Destination {
  id: string;
  city: string;
  country: string;
  heroImage?: string;
  tagline?: string;
}

interface DestinationContent {
  key_pros?: string[];
  considerations?: string[]; // Changed from key_cons to considerations
  images?: Array<{ url: string; caption?: string } | string>;
  flights_transportation?: number | null;
  food_beverage?: number | null;
  activities_meetings?: number | null;
  miscellaneous?: number | null;
}

interface VenueData {
  venue: any;
  shortlistItem: any;
}

interface ComparisonProps {
  destinations: Destination[];
  approvedVenuesByDestination: Record<string, Hotel[]>;
  venueDataByDestination: Record<string, VenueData[]>;
  destinationContentByDestination: Record<string, DestinationContent>;
  groupSize?: number;
  budgetMin?: number;
  budgetMax?: number;
  shortlistedHotelIds?: Set<string>;
  shareLinkToken?: string;
  sessionId?: string;
  eventId?: string;
  onAddToShortlist?: (hotel: HotelWithDestination) => void;
  onNavigate?: (id: string) => void;
}

interface HotelWithDestination extends Hotel {
  destinationId: string;
  destinationCity: string;
  destinationCountry: string;
  destinationImage?: string;
}

export function Comparison({
  destinations,
  approvedVenuesByDestination,
  venueDataByDestination,
  destinationContentByDestination,
  groupSize = 50,
  budgetMin,
  budgetMax,
  shortlistedHotelIds = new Set(),
  shareLinkToken,
  sessionId,
  eventId,
  onAddToShortlist,
  onNavigate
}: ComparisonProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isComparisonScrolled, setIsComparisonScrolled] = useState(false);
  const [comparisonColumnWidths, setComparisonColumnWidths] = useState<number[]>([]);
  // Which comparison view: the clean financial summary (default) or the detailed category table
  const [comparisonView, setComparisonView] = useState<'financial' | 'detailed'>('financial');

  // Thin wrapper around the shared formatter so existing call-sites keep
  // their `formatAmount(value, currency)` shape. Rounds before formatting
  // because callers pass raw values.
  const formatAmount = (amount: number, currencyCode: string = 'USD'): string =>
    formatCurrency(Math.round(amount), currencyCode);

  const formatRoomRateDetail = (
    roomCount: number,
    roomType: 'SGL' | 'DBL',
    nightlyRate: number,
    currencyCode: string,
    nights: number
  ): string => {
    const nightLabel = nights === 1 ? 'night' : 'nights';
    return `${roomCount}x ${roomType} @ ${formatAmount(nightlyRate, currencyCode)}/night x ${nights} ${nightLabel}`;
  };

  // Budget data state
  const [fullBudget, setFullBudget] = useState<any>(null);
  const [exchangeRates, setExchangeRates] = useState<any>(null);
  const [budgetBreakdowns, setBudgetBreakdowns] = useState<Record<string, any>>({});
  const [budgetLoading, setBudgetLoading] = useState(false);

  const updateComparisonColumnWidths = () => {
    const headerCells = scrollContainerRef.current?.querySelectorAll('table thead tr:first-child th');
    if (!headerCells?.length) return;

    const widths = Array.from(headerCells).map(cell => Math.round(cell.getBoundingClientRect().width));
    setComparisonColumnWidths(prev => {
      const unchanged = prev.length === widths.length && prev.every((width, index) => Math.abs(width - widths[index]) <= 1);
      return unchanged ? prev : widths;
    });
  };

  const updateScrollProgress = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollTop, scrollWidth, clientWidth } = scrollContainerRef.current;
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
      setScrollProgress(progress);
      setIsComparisonScrolled(scrollTop > 120);
      updateComparisonColumnWidths();
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollProgress);
      // Initial calculation
      updateScrollProgress();
      window.addEventListener('resize', updateScrollProgress);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', updateScrollProgress);
      }
      window.removeEventListener('resize', updateScrollProgress);
    };
  }, []);

  // Fetch exchange rates on mount
  useEffect(() => {
    if (shareLinkToken) {
      proposalService.getExchangeRates()
        .then(data => {
          const exchangeRatesData = data.rates || data;
          setExchangeRates(exchangeRatesData);
        })
        .catch(err => console.error('Failed to fetch exchange rates:', err));
    }
  }, [shareLinkToken]);

  // Fetch full budget when eventId is available
  useEffect(() => {
    if (eventId && shareLinkToken && sessionId) {
      setBudgetLoading(true);
      proposalService.getFullBudget(shareLinkToken, eventId, sessionId)
        .then(data => {
          setFullBudget(data.budget);
          setBudgetLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch full budget:', err);
          setBudgetLoading(false);
        });
    }
  }, [eventId, shareLinkToken, sessionId]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300; // Width of one column
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Flatten all approved hotels from all destinations into a single array
  // Each hotel gets its destination info attached. This is the raw
  // destination-ordered list — it's used for budget fetching and as the
  // input to the budget-sorted `allHotels` below.
  const allHotelsUnsorted = useMemo<HotelWithDestination[]>(() => {
    const hotels: HotelWithDestination[] = [];
    destinations.forEach(dest => {
      const venues = approvedVenuesByDestination[dest.id] || [];
      venues.forEach(venue => {
        const content = destinationContentByDestination[dest.id];
        const destinationImage = content?.images && content.images.length > 0
          ? (typeof content.images[0] === 'string' ? content.images[0] : content.images[0].url)
          : dest.heroImage;

        hotels.push({
          ...venue,
          destinationId: dest.id,
          destinationCity: dest.city,
          destinationCountry: dest.country,
          destinationImage: destinationImage,
        });
      });
    });
    return hotels;
  }, [destinations, approvedVenuesByDestination, destinationContentByDestination]);

  // Fetch venue budget breakdowns for all hotels
  useEffect(() => {
    if (allHotelsUnsorted.length > 0 && shareLinkToken && sessionId) {
      const fetchBreakdowns = async () => {
        setBudgetLoading(true);
        const breakdowns: Record<string, any> = {};

        for (const hotel of allHotelsUnsorted) {
          const venueData = venueDataByDestination[hotel.destinationId] || [];
          const venueInfo = venueData.find(v => v.venue.id === hotel.id);
          if (venueInfo?.shortlistItem?.id) {
            try {
              const data = await proposalService.getVenueBudgetBreakdown(
                shareLinkToken,
                venueInfo.shortlistItem.id,
                sessionId
              );
              breakdowns[hotel.id] = data;
            } catch (err) {
              console.error(`Failed to fetch budget breakdown for hotel ${hotel.id}:`, err);
            }
          }
        }

        setBudgetBreakdowns(breakdowns);
        setBudgetLoading(false);
      };

      fetchBreakdowns();
    }
  }, [allHotelsUnsorted, shareLinkToken, sessionId, venueDataByDestination]);

  // Helper to get pros for a destination
  const getDestinationPros = (destId: string): string[] => {
    const content = destinationContentByDestination[destId];
    return Array.isArray(content?.key_pros) ? content.key_pros : [];
  };

  // Helper to get considerations for a destination
  const getDestinationCons = (destId: string): string[] => {
    const content = destinationContentByDestination[destId];
    return Array.isArray(content?.considerations) ? content.considerations : [];
  };

  // Calculate budget breakdown for a hotel (matching Destination component logic)
  const calculateBudget = useCallback((hotel: HotelWithDestination) => {
    const budgetBreakdown = budgetBreakdowns[hotel.id];

    // If we have budget breakdown data, use it (same logic as Destination component)
    if (budgetBreakdown && fullBudget) {
      const breakdown = budgetBreakdown;
      const settings = fullBudget.settings;
      const nights = fullBudget?.nights ?? 0;
      const attendees = fullBudget?.attendees ?? groupSize;
      const eventCurrency = fullBudget?.base_currency || 'USD';
      const venueCurrency = breakdown?.otherCosts?.venue_currency || 'USD';

      const result = calculateProposalBudget({ breakdown, settings, nights, attendees,
        eventCurrency, exchangeRates: normalizeBudgetRates(exchangeRates) });
      const { accommodation: accommodationTotal, activities: activitiesTotal, meals: mealsTotal,
        meetings: meetingsTotal, flightCost, groundTransport, miscellaneous: miscTotal,
        subtotal, contingencyAmount, planningOverheadAmount, profitMarginAmount,
        totalProgramCost, perAttendeeCost } = result;
      const rb = breakdown.roomBreakdown;
      const accommodationDetails = [
        rb?.single_rooms > 0 && formatRoomRateDetail(rb.single_rooms, 'SGL', result.singleRoomRate, eventCurrency, result.roomNights),
        rb?.double_rooms > 0 && formatRoomRateDetail(rb.double_rooms, 'DBL', result.doubleRoomRate, eventCurrency, result.roomNights),
      ].filter(Boolean).join(', ');

      return {
        totalEstimated: result.available ? totalProgramCost : null,
        perPerson: result.available ? perAttendeeCost : null,
        breakdown: {
          accommodation: accommodationTotal,
          accommodationDetails,
          flightCost,
          groundTransport,
          meals: mealsTotal,
          meetings: meetingsTotal,
          activities: activitiesTotal,
          misc: miscTotal,
        },
        totals: {
          subtotal,
          contingency: contingencyAmount,
          planningOverhead: planningOverheadAmount,
          profitMargin: profitMarginAmount,
          totalProgramCost,
        },
        settings,
        eventCurrency,
      };
    }

    // Fallback to simple calculation using destination content
    const destinationContent = destinationContentByDestination[hotel.destinationId];
    const venueData = venueDataByDestination[hotel.destinationId] || [];
    const venueInfo = venueData.find(v => v.venue.id === hotel.id);

    let accommodation = null;
    if (venueInfo) {
      const totalCost = venueInfo.shortlistItem.override_total_cost ?? venueInfo.shortlistItem.cvent_total_cost;
      if (totalCost !== null && totalCost !== undefined) {
        accommodation = Math.round(totalCost);
      }
    }

    const foodAndBev = destinationContent?.food_beverage !== null && destinationContent?.food_beverage !== undefined
      ? Math.round(destinationContent.food_beverage / 100)
      : null;

    const activities = destinationContent?.activities_meetings !== null && destinationContent?.activities_meetings !== undefined
      ? Math.round(destinationContent.activities_meetings / 100)
      : null;

    const transport = destinationContent?.flights_transportation !== null && destinationContent?.flights_transportation !== undefined
      ? Math.round(destinationContent.flights_transportation / 100)
      : null;

    const misc = destinationContent?.miscellaneous !== null && destinationContent?.miscellaneous !== undefined
      ? Math.round(destinationContent.miscellaneous / 100)
      : null;

    const hasAllRequired = accommodation !== null && foodAndBev !== null && activities !== null && transport !== null && misc !== null;
    const totalEstimated = hasAllRequired && accommodation !== null
      ? accommodation + foodAndBev + activities + transport + misc
      : null;
    const perPerson = hasAllRequired && totalEstimated !== null && groupSize > 0
      ? Math.round(totalEstimated / groupSize)
      : null;

    // In fallback mode, return null for missing values (will show —)
    // Only return 0 if the value is explicitly 0
    return {
      totalEstimated,
      perPerson,
      breakdown: {
        accommodation,
        flightCost: null,
        groundTransport: transport,
        meals: foodAndBev,
        meetings: null,
        activities,
        misc
      },
      totals: null,
      settings: null,
      eventCurrency: 'USD',
    };
  }, [budgetBreakdowns, fullBudget, exchangeRates, groupSize, venueDataByDestination, destinationContentByDestination]);

  // Sort venues within each destination by total estimated budget (lowest → highest).
  // Destination order is preserved. Hotels with no budget yet (or nulls) go to
  // the end of their destination group so known-budget hotels always appear first.
  const allHotels = useMemo<HotelWithDestination[]>(() => {
    const byDest = new Map<string, HotelWithDestination[]>();
    const destOrder: string[] = [];
    for (const hotel of allHotelsUnsorted) {
      if (!byDest.has(hotel.destinationId)) {
        byDest.set(hotel.destinationId, []);
        destOrder.push(hotel.destinationId);
      }
      byDest.get(hotel.destinationId)!.push(hotel);
    }
    for (const destId of destOrder) {
      const group = byDest.get(destId)!;
      group.sort((a, b) => {
        const ta = calculateBudget(a).totalEstimated;
        const tb = calculateBudget(b).totalEstimated;
        if (ta == null && tb == null) return 0;
        if (ta == null) return 1;
        if (tb == null) return -1;
        return ta - tb;
      });
    }
    return destOrder.flatMap(id => byDest.get(id)!);
  }, [allHotelsUnsorted, calculateBudget]);

  const comparisonBudgetNotes = useMemo<Record<string, string>>(() => {
    const notes: Record<string, string> = {};

    for (const hotel of allHotels) {
      const venueData = venueDataByDestination[hotel.destinationId] || [];
      const venueInfo = venueData.find(v => v.venue.id === hotel.id);
      const note = venueInfo?.shortlistItem?.comparison_budget_note?.trim();
      if (note) {
        notes[`${hotel.destinationId}:${hotel.id}`] = note;
      }
    }

    return notes;
  }, [allHotels, venueDataByDestination]);

  const hasComparisonBudgetNotes = Object.keys(comparisonBudgetNotes).length > 0;

  // Financial summary view: all hotels sorted by total (lowest → highest), with
  // per-person, land, flights + total, and the cheapest flagged "Lowest".
  const financialSummary = useMemo(() => {
    const rows = allHotels.map(h => {
      const b = calculateBudget(h);
      return {
        hotel: h,
        key: `${h.destinationId}:${h.id}`,
        total: (b.totalEstimated ?? null) as number | null,
        perPerson: (b.perPerson ?? null) as number | null,
        ...getDisplayedFinancialCostSplit(
          b.totalEstimated,
          // A saved blank flight rate means zero, just as in the budget builder;
          // an absent breakdown/field means unknown, not free flights.
          budgetBreakdowns[h.id]?.otherCosts?.flight_cost_per_attendee !== undefined
            ? b.breakdown.flightCost
            : null,
        ),
        currency: (b.eventCurrency ?? 'USD') as string,
      };
    });
    const withTotal = rows.filter(r => r.total != null).sort((a, b) => (a.total as number) - (b.total as number));
    const withoutTotal = rows.filter(r => r.total == null);
    return {
      rows: [...withTotal, ...withoutTotal],
      lowestKey: withTotal.length ? withTotal[0].key : null,
      currency: withTotal[0]?.currency || rows[0]?.currency || 'USD',
    };
  }, [allHotels, calculateBudget, budgetBreakdowns]);

  const unavailableFx = fullBudget && Object.values(budgetBreakdowns).some((b: any) =>
    !Number.isFinite(budgetFxRate(b.otherCosts?.venue_currency || 'USD', fullBudget.base_currency || 'USD', normalizeBudgetRates(exchangeRates))));
  const summaryAttendees = (fullBudget?.attendees ?? groupSize) || 50;
  const summaryNights = fullBudget?.nights ?? null;
  const summaryCurrency = fullBudget?.base_currency || financialSummary.currency;
  const comparisonGridTemplate = comparisonColumnWidths.length === allHotels.length + 1
    ? comparisonColumnWidths.map(width => `${width}px`).join(' ')
    : `200px repeat(${allHotels.length}, minmax(300px, 1fr))`;
  const comparisonGridWidth = comparisonColumnWidths.length === allHotels.length + 1
    ? `${comparisonColumnWidths.reduce((sum, width) => sum + width, 0)}px`
    : `max(100%, ${200 + allHotels.length * 300}px)`;

  useEffect(() => {
    updateScrollProgress();
  }, [allHotels.length]);

  if (allHotels.length === 0) {
    return (
      <div className="h-full overflow-y-auto md:overflow-hidden md:flex md:flex-col bg-cream text-teal px-6 md:px-12 py-8 md:py-12 hide-scrollbar">
        <div className="max-w-7xl mx-auto w-full md:flex md:flex-col md:min-h-0 md:flex-1">
          <div className="text-center py-12">
            <p className="text-teal/60">No approved hotels available for comparison.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto md:overflow-hidden md:flex md:flex-col bg-cream text-teal px-6 md:px-12 py-8 md:py-12 hide-scrollbar">
      <style>{`
        @media (min-width: 768px) {
          .desktop-hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .desktop-hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        }
      `}</style>
      <div className="max-w-7xl mx-auto w-full md:flex md:flex-col md:min-h-0 md:flex-1">
        <div className="mb-8 md:mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4 px-2 md:px-0 md:flex-shrink-0">
           <div>
             <h1 className="text-4xl font-serif font-bold mb-4">Side-by-Side Comparison</h1>
             <p className="text-teal/60">
               Shortlist your favorite option below — estimates are based on {groupSize || 50} people.
             </p>
           </div>
           <div className="flex flex-col md:items-end gap-3 md:pb-1 w-full md:w-auto">
             {/* View toggle: Financial Summary (default) vs Detailed */}
             <div className="inline-flex self-start md:self-auto rounded-md border border-teal/20 bg-cream p-1" role="group" aria-label="Comparison view">
               <button
                 type="button"
                 onClick={() => setComparisonView('financial')}
                 className={`px-3 py-1.5 text-sm font-semibold rounded transition-colors ${comparisonView === 'financial' ? 'text-white shadow-sm' : 'text-teal/70 hover:bg-teal/5'}`}
                 style={comparisonView === 'financial' ? { backgroundColor: proposalTokens.colors.gold } : undefined}
                 aria-pressed={comparisonView === 'financial'}
               >
                 Financial Summary
               </button>
               <button
                 type="button"
                 onClick={() => setComparisonView('detailed')}
                 className={`px-3 py-1.5 text-sm font-semibold rounded transition-colors ${comparisonView === 'detailed' ? 'text-white shadow-sm' : 'text-teal/70 hover:bg-teal/5'}`}
                 style={comparisonView === 'detailed' ? { backgroundColor: proposalTokens.colors.gold } : undefined}
                 aria-pressed={comparisonView === 'detailed'}
               >
                 Detailed
               </button>
             </div>
             {/* Horizontal scroll arrows — detailed view only */}
             {comparisonView === 'detailed' && (
               <div className="hidden md:flex items-center gap-2">
                 <button onClick={() => scroll('left')} className="p-2 rounded-full border border-teal/20 bg-cream hover:bg-teal hover:text-white transition-colors" aria-label="Scroll left">
                   <ChevronLeft className="w-6 h-6" />
                 </button>
                 <button onClick={() => scroll('right')} className="p-2 rounded-full border border-teal/20 bg-cream hover:bg-teal hover:text-white transition-colors" aria-label="Scroll right">
                   <ChevronRight className="w-6 h-6" />
                 </button>
               </div>
             )}
           </div>
           {/* Mobile Scroll Indicator — detailed view only */}
           {comparisonView === 'detailed' && (
             <div className="md:hidden w-full h-1 bg-black/10 rounded-full overflow-hidden mt-4">
               <div
                 className="h-full bg-teal transition-all duration-200 ease-out"
                 style={{ width: `${Math.max(5, scrollProgress)}%` }}
               />
             </div>
           )}
        </div>

        {/* ── Financial Summary view (default) ── */}
        {unavailableFx && <p role="status" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{BUDGET_FX_UNAVAILABLE}</p>}
        {comparisonView === 'financial' && (
          <div className="md:flex md:flex-col md:min-h-0 md:flex-1">
            <div className="md:overflow-y-auto md:flex-1 hide-scrollbar">
              <div className="financial-cost-summary">
                {/* Sticky so the column names stay visible while scrolling a long list of hotels. */}
                <div className="financial-cost-heading bg-cream" aria-hidden="true">
                  <span />
                  <span>Land Costs</span>
                  <span>Airfare</span>
                  <span className="financial-cost-totals-start">Total Program</span>
                  <span>Per Person</span>
                </div>
                {financialSummary.rows.map(({ hotel, key, total, perPerson, land, flights, currency }) => (
                  <div
                    key={key}
                    onClick={() => onNavigate?.(hotel.destinationId)}
                    className={`financial-cost-row ${onNavigate ? 'cursor-pointer hover:bg-black/[0.02]' : ''}`}
                  >
                    <div className="financial-cost-hotel">
                      <div className="min-w-0">
                        {onNavigate ? (
                          <button type="button" className="text-left font-semibold hover:underline" onClick={(event) => { event.stopPropagation(); onNavigate(hotel.destinationId); }}>
                            {hotel.name}
                          </button>
                        ) : <p className="font-semibold">{hotel.name}</p>}
                        <p className="text-xs text-teal/40">{hotel.destinationCity}{hotel.destinationCountry ? `, ${hotel.destinationCountry}` : ''}</p>
                      </div>
                      {key === financialSummary.lowestKey && (
                        <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Lowest</span>
                      )}
                    </div>
                    <dl className="financial-cost-values">
                      {[
                        { label: 'Land Costs', amount: land, emphasize: false, className: '' },
                        { label: 'Airfare', amount: flights, emphasize: false, className: '' },
                        // The two totals are set apart from the cost breakdown by a divider.
                        { label: 'Total Program', amount: total, emphasize: true, className: 'financial-cost-totals-start' },
                        { label: 'Per Person', amount: perPerson, emphasize: true, className: 'financial-cost-per-person' },
                      ].map(({ label, amount, emphasize, className }) => (
                        <div key={label} className={`financial-cost-value ${className}`.trim()}>
                          <dt>{label}</dt>
                          <dd className={emphasize ? 'financial-cost-total' : undefined}>{amount != null ? formatAmount(amount, currency) : '—'}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
              {financialSummary.rows.some(row => row.land == null) && (
                <p className="text-xs text-teal/60 mt-3 px-2">— A separate flight breakdown is not available for this option.</p>
              )}
            </div>

            {/* Summary footer cards — compact, teal w/ white text (per client feedback:
                matches the climate "View More" tile; text sized like the summary rows) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 md:flex-shrink-0">
              <div className="rounded-xl bg-teal text-white shadow-md py-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/60">Attendees</p>
                <p className="text-base font-bold">{summaryAttendees}</p>
              </div>
              <div className="rounded-xl bg-teal text-white shadow-md py-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/60">Event Duration</p>
                <p className="text-base font-bold">{summaryNights != null ? `${summaryNights} ${summaryNights === 1 ? 'night' : 'nights'}` : '—'}</p>
              </div>
              <div className="rounded-xl bg-teal text-white shadow-md py-3 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wide text-white/60">Event Currency</p>
                <p className="text-base font-bold">{summaryCurrency}</p>
              </div>
            </div>

            {/* Next Steps Button */}
            {onNavigate && (
              <div className="mt-6 flex justify-center md:flex-shrink-0">
                <button
                  onClick={() => onNavigate('sign-off')}
                  className="px-5 py-2 text-sm font-bold rounded-md hover:opacity-90 transition-colors flex items-center gap-1.5 border border-black/20 hover:bg-black/5"
                  style={{ color: proposalTokens.colors.teal }}
                >
                  Next Steps
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Detailed category table view ── */}
        {comparisonView === 'detailed' && (<>
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto pb-8 desktop-hide-scrollbar md:overflow-auto md:min-h-0 md:flex-1"
        >
          <div className="hidden md:block sticky top-0 h-0 z-50">
            <div
              className={`grid bg-cream border-b border-black/10 shadow-[0_8px_18px_rgba(0,0,0,0.08)] transition-opacity duration-150 ${isComparisonScrolled ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
              style={{ gridTemplateColumns: comparisonGridTemplate, width: comparisonGridWidth, minWidth: `${200 + allHotels.length * 300}px` }}
            >
              <div className="sticky left-0 z-10 bg-cream p-2 border-b border-black/10" aria-hidden="true" />
              {allHotels.map(hotel => {
                const budget = calculateBudget(hotel);

                return (
                  <div key={`${hotel.id}-sticky-summary`} className="p-2 text-center bg-cream border-b border-black/10">
                    <h3 className="text-base font-serif font-bold text-teal truncate" title={hotel.name}>{hotel.name}</h3>
                    <div className="mt-1 flex items-center justify-center gap-3 text-xs text-teal/70 whitespace-nowrap">
                      <span>
                        <span className="uppercase tracking-wide text-teal/45">Total</span>{' '}
                        <strong className="text-teal font-bold">
                          {budget.totalEstimated !== null && budget.totalEstimated !== undefined && !isNaN(budget.totalEstimated)
                            ? formatAmount(budget.totalEstimated, budget.eventCurrency)
                            : '—'}
                        </strong>
                      </span>
                      <span className="h-4 border-l border-black/15" aria-hidden="true" />
                      <span>
                        <span className="uppercase tracking-wide text-teal/45">PP</span>{' '}
                        <strong className="text-teal font-semibold">
                          {budget.perPerson !== null && budget.perPerson !== undefined && !isNaN(budget.perPerson)
                            ? formatAmount(budget.perPerson, budget.eventCurrency)
                            : '—'}
                        </strong>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <table className="w-full text-left border-collapse table-fixed">
             <thead>
                <tr>
                   <th className="py-4 pr-2 pl-0 md:p-4 w-[130px] min-w-[130px] md:w-[200px] md:min-w-[200px] align-top">
                     <span className="sr-only">Properties</span>
                   </th>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     const imageUrl = hotel.image || (hotel.images && hotel.images.length > 0 ? hotel.images[0] : null) || hotel.destinationImage || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80';

                     return (
                       <th key={hotel.id} className="p-4 w-[300px] min-w-[300px] align-top text-center">
                          <img src={imageUrl} className="w-full h-32 object-cover rounded-xl mb-4" alt={`${hotel.name}, ${hotel.destinationCity}`} loading="lazy" />
                          <h3 className="text-xl font-serif font-bold text-teal truncate text-center" title={hotel.name}>{hotel.name}</h3>
                            <div className="bg-white rounded-lg p-3 border border-black/10 text-center shadow-sm mt-4">
                              <span className="block text-xs text-teal/50 uppercase">Total Est.</span>
                              <span className="block text-xl font-bold text-teal">
                                {budget.totalEstimated !== null && budget.totalEstimated !== undefined && !isNaN(budget.totalEstimated)
                                  ? formatAmount(budget.totalEstimated, budget.eventCurrency)
                                  : '—'}
                              </span>
                              <span className="block text-[11px] text-teal/45 uppercase mt-2">Per Person</span>
                              <span className="block text-sm font-semibold text-teal">
                                {budget.perPerson !== null && budget.perPerson !== undefined && !isNaN(budget.perPerson)
                                  ? formatAmount(budget.perPerson, budget.eventCurrency)
                                  : '—'}
                              </span>
                            </div>
                       </th>
                     );
                   })}
                </tr>
             </thead>
             <tbody className="divide-y divide-black/10">
                {/* Destination */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Destination</td>
                   {allHotels.map(hotel => (
                      <td key={hotel.id} className="p-4 text-teal font-medium">{hotel.destinationCity}, {hotel.destinationCountry}</td>
                   ))}
                </tr>
                {/* Additional comparison details */}
                {hasComparisonBudgetNotes && (
                  <tr>
                    <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">
                      Additional Details
                    </td>
                    {allHotels.map(hotel => {
                      const note = comparisonBudgetNotes[`${hotel.destinationId}:${hotel.id}`];
                      return (
                        <td key={hotel.id} className="p-4 text-sm text-teal/70 leading-relaxed whitespace-pre-wrap">
                          {note ? (
                            note
                          ) : (
                            <span className="text-teal/30">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )}
                {/* Accommodation */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Accommodation</td>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     return (
                       <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                         {budget.breakdown.accommodation !== null && budget.breakdown.accommodation !== undefined && !isNaN(budget.breakdown.accommodation)
                           ? formatAmount(budget.breakdown.accommodation, budget.eventCurrency)
                           : '—'}
                         {budget.breakdown.accommodationDetails && (
                           <div className="text-xs text-teal/40 mt-1">{budget.breakdown.accommodationDetails}</div>
                         )}
                       </td>
                     );
                   })}
                </tr>
                {/* Flight Cost */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Flight Cost</td>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     return (
                       <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                         {budget.breakdown.flightCost !== null && budget.breakdown.flightCost !== undefined && !isNaN(budget.breakdown.flightCost)
                           ? formatAmount(budget.breakdown.flightCost, budget.eventCurrency)
                           : '—'}
                       </td>
                     );
                   })}
                </tr>
                {/* Ground Transport */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Ground Transport</td>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     return (
                       <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                         {budget.breakdown.groundTransport !== null && budget.breakdown.groundTransport !== undefined && !isNaN(budget.breakdown.groundTransport)
                           ? formatAmount(budget.breakdown.groundTransport, budget.eventCurrency)
                           : '—'}
                       </td>
                     );
                   })}
                </tr>
                {/* Meals */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Meals</td>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     return (
                       <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                         {budget.breakdown.meals !== null && budget.breakdown.meals !== undefined && !isNaN(budget.breakdown.meals)
                           ? formatAmount(budget.breakdown.meals, budget.eventCurrency)
                           : '—'}
                       </td>
                     );
                   })}
                </tr>
                {/* Meetings */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Meetings</td>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     return (
                       <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                         {budget.breakdown.meetings !== null && budget.breakdown.meetings !== undefined && !isNaN(budget.breakdown.meetings)
                           ? formatAmount(budget.breakdown.meetings, budget.eventCurrency)
                           : '—'}
                       </td>
                     );
                   })}
                </tr>
                {/* Activities */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Activities</td>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     return (
                       <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                         {budget.breakdown.activities !== null && budget.breakdown.activities !== undefined && !isNaN(budget.breakdown.activities)
                           ? formatAmount(budget.breakdown.activities, budget.eventCurrency)
                           : '—'}
                       </td>
                     );
                   })}
                </tr>
                {/* Misc */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">Misc</td>
                   {allHotels.map(hotel => {
                     const budget = calculateBudget(hotel);
                     return (
                       <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                         {budget.breakdown.misc !== null && budget.breakdown.misc !== undefined && !isNaN(budget.breakdown.misc)
                           ? formatAmount(budget.breakdown.misc, budget.eventCurrency)
                           : '—'}
                       </td>
                     );
                   })}
                </tr>
                {/* Subtotal */}
                {allHotels.some(hotel => calculateBudget(hotel).totals !== null) && (
                  <tr>
                     <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal md:sticky md:left-0 md:bg-cream md:z-10 border-t-2 border-black/20 border-b border-black/10">Subtotal</td>
                     {allHotels.map(hotel => {
                       const budget = calculateBudget(hotel);
                       return (
                         <td key={hotel.id} className="p-4 text-teal font-semibold border-t-2 border-black/20">
                           {budget.totals?.subtotal !== null && budget.totals?.subtotal !== undefined ? formatAmount(budget.totals.subtotal, budget.eventCurrency) : '—'}
                         </td>
                       );
                     })}
                  </tr>
                )}
                {/* Contingency */}
                {allHotels.some(hotel => {
                  const budget = calculateBudget(hotel);
                  return budget.totals?.contingency !== null && budget.totals?.contingency !== undefined && budget.totals.contingency > 0;
                }) && (
                  <tr>
                     <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">
                       Contingency
                       {allHotels[0] && calculateBudget(allHotels[0]).settings && (
                         <div className="text-xs font-normal text-teal/40 mt-0.5">
                           {calculateBudget(allHotels[0]).settings.contingency_fixed != null ? 'Fixed' : `${calculateBudget(allHotels[0]).settings.contingency_percent}%`}
                         </div>
                       )}
                     </td>
                     {allHotels.map(hotel => {
                       const budget = calculateBudget(hotel);
                       const contingency = budget.totals?.contingency;
                       return (
                         <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                           {contingency !== null && contingency !== undefined && contingency > 0 ? formatAmount(contingency, budget.eventCurrency) : '—'}
                         </td>
                       );
                     })}
                  </tr>
                )}
                {/* Planning Overhead */}
                {allHotels.some(hotel => {
                  const budget = calculateBudget(hotel);
                  return budget.totals?.planningOverhead !== null && budget.totals?.planningOverhead !== undefined && budget.totals.planningOverhead > 0;
                }) && (
                  <tr>
                     <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">
                       Planning Overhead
                       {allHotels[0] && calculateBudget(allHotels[0]).settings && (() => {
                         const settings = calculateBudget(allHotels[0]).settings;
                         return (
                           <div className="text-xs font-normal text-teal/40 mt-0.5">
                             {settings.planning_overhead_fixed !== null
                               ? formatAmount(settings.planning_overhead_fixed / 100, calculateBudget(allHotels[0]).eventCurrency)
                               : `${settings.planning_overhead_percent}%`}
                           </div>
                         );
                       })()}
                     </td>
                     {allHotels.map(hotel => {
                       const budget = calculateBudget(hotel);
                       const planningOverhead = budget.totals?.planningOverhead;
                       return (
                         <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                           {planningOverhead !== null && planningOverhead !== undefined && planningOverhead > 0 ? formatAmount(planningOverhead, budget.eventCurrency) : '—'}
                         </td>
                       );
                     })}
                  </tr>
                )}
                {/* Profit Margin */}
                {allHotels.some(hotel => {
                  const budget = calculateBudget(hotel);
                  return budget.totals?.profitMargin !== null && budget.totals?.profitMargin !== undefined && budget.totals.profitMargin > 0;
                }) && (
                  <tr>
                     <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 border-b border-black/10">
                       Profit Margin
                       {allHotels[0] && calculateBudget(allHotels[0]).settings && (() => {
                         const settings = calculateBudget(allHotels[0]).settings;
                         return (
                           <div className="text-xs font-normal text-teal/40 mt-0.5">
                             {settings.profit_margin_fixed !== null
                               ? formatAmount(settings.profit_margin_fixed / 100, calculateBudget(allHotels[0]).eventCurrency)
                               : `${settings.profit_margin_percent}%`}
                           </div>
                         );
                       })()}
                     </td>
                     {allHotels.map(hotel => {
                       const budget = calculateBudget(hotel);
                       const profitMargin = budget.totals?.profitMargin;
                       return (
                         <td key={hotel.id} className="p-4 text-teal/60 text-sm">
                           {profitMargin !== null && profitMargin !== undefined && profitMargin > 0 ? formatAmount(profitMargin, budget.eventCurrency) : '—'}
                         </td>
                       );
                     })}
                  </tr>
                )}
                {/* Total Program Cost */}
                {allHotels.some(hotel => calculateBudget(hotel).totals?.totalProgramCost !== null) && (
                  <tr>
                     <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal md:sticky md:left-0 md:bg-cream md:z-10 border-t-2 border-black/20 border-b border-black/10">
                       Total Program Cost
                       {allHotels[0] && calculateBudget(allHotels[0]).eventCurrency && (
                         <div className="text-xs font-normal text-teal/40 mt-0.5">
                           ({calculateBudget(allHotels[0]).eventCurrency})
                         </div>
                       )}
                     </td>
                     {allHotels.map(hotel => {
                       const budget = calculateBudget(hotel);
                       return (
                         <td key={hotel.id} className="p-4 text-teal font-bold text-lg border-t-2 border-black/20">
                           {budget.totals?.totalProgramCost !== null && budget.totals?.totalProgramCost !== undefined ? formatAmount(budget.totals.totalProgramCost, budget.eventCurrency) : '—'}
                         </td>
                       );
                     })}
                  </tr>
                )}
                {/* Pros */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 align-top pt-6 border-b border-black/10">Key Pros</td>
                   {allHotels.map(hotel => {
                     const pros = getDestinationPros(hotel.destinationId);
                     return (
                       <td key={hotel.id} className="p-4 align-top pt-6">
                          <ul className="space-y-2">
                            {pros.length > 0 ? (
                              pros.map((pro, i) => (
                                <li key={i} className="flex items-start text-sm text-teal">
                                   <Check className="w-4 h-4 text-teal mr-2 shrink-0 mt-0.5" /> {pro}
                                </li>
                              ))
                            ) : (
                              <li className="flex items-start text-sm text-teal/60">
                                <span>No pros available</span>
                              </li>
                            )}
                          </ul>
                       </td>
                     );
                   })}
                </tr>
                {/* Cons */}
                <tr>
                   <td className="py-4 pr-2 pl-0 md:p-4 font-bold text-teal/60 md:sticky md:left-0 md:bg-cream md:z-10 align-top pt-6 border-b border-black/10">Considerations</td>
                   {allHotels.map(hotel => {
                     const cons = getDestinationCons(hotel.destinationId);
                     return (
                       <td key={hotel.id} className="p-4 align-top pt-6 border-b border-black/10">
                          <ul className="space-y-2">
                            {cons.length > 0 ? (
                              cons.map((con, i) => (
                                <li key={i} className="flex items-start text-sm text-teal/60">
                                   <X className="w-4 h-4 text-red-400 mr-2 shrink-0 mt-0.5" /> {con}
                                </li>
                              ))
                            ) : (
                              <li className="flex items-start text-sm text-teal/60">
                                <span>No considerations available</span>
                              </li>
                            )}
                          </ul>
                       </td>
                     );
                   })}
                </tr>
                {/* Add to Shortlist Button Row */}
                {onAddToShortlist && (
                  <tr>
                    <td className="py-4 pr-2 pl-0 md:p-4 md:sticky md:left-0 md:bg-cream md:z-10"></td>
                    {allHotels.map(hotel => {
                      const isShortlisted = shortlistedHotelIds.has(hotel.id);
                      return (
                        <td key={hotel.id} className="p-4 pt-6">
                          <button
                            onClick={() => !isShortlisted && onAddToShortlist(hotel)}
                            disabled={isShortlisted}
                            className={`
                              px-6 py-2 text-sm font-bold rounded-md transition-colors
                              ${isShortlisted
                                ? 'bg-teal/20 text-teal/60 cursor-not-allowed opacity-60'
                                : 'hover:opacity-90'
                              }
                            `}
                            style={isShortlisted
                              ? {}
                              : {
                                  backgroundColor: proposalTokens.colors.gold,
                                  color: '#000000'
                                }
                            }
                          >
                            {isShortlisted ? 'Added to Shortlist' : 'Add to Shortlist'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                )}
             </tbody>
          </table>
        </div>

        {/* Next Steps Button */}
        {onNavigate && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => onNavigate('sign-off')}
              className="px-5 py-2 text-sm font-bold rounded-md hover:opacity-90 transition-colors flex items-center gap-1.5 border border-black/20 hover:bg-black/5"
              style={{ color: proposalTokens.colors.teal }}
            >
              Next Steps
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}
